import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMapPin, FiCreditCard, FiCheck } from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import API from '../services/api';

const Checkout = () => {
  const { cart, cartTotal, clearCart, user } = useApp();
  const navigate = useNavigate();
  const [address, setAddress] = useState(user?.address || '');
  const [paymentMethod, setPaymentMethod] = useState('wave');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const paymentMethods = [
    { id: 'wave', label: 'Wave', color: 'bg-blue-500' },
    { id: 'orange_money', label: 'Orange Money', color: 'bg-orange-500' },
    { id: 'free_money', label: 'Free Money', color: 'bg-red-500' },
    { id: 'card', label: 'Carte Bancaire', color: 'bg-gray-700' },
  ];

  const handleOrder = async () => {
    if (!address) {
      setError('Veuillez entrer votre adresse de livraison');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const orderData = {
        products: cart.map(item => ({
          product: item._id,
          quantity: item.quantity,
          size: item.size,
        })),
        shippingAddress: address,
        paymentMethod,
      };

      const orderRes = await API.post('/orders', orderData);
      const orderId = orderRes.data._id;

      const paymentRes = await API.post('/payment/initiate', { orderId });

      if (paymentRes.data.redirect_url) {
        clearCart();
        window.location.href = paymentRes.data.redirect_url;
      } else {
        clearCart();
        navigate('/mes-commandes');
      }
    } catch (err) {
      setError('Erreur lors de la commande. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">
        Finaliser la <span className="text-primary">commande</span>
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

        {/* Formulaire */}
        <div className="space-y-6">

          {/* Adresse */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="font-bold text-gray-800 text-lg mb-4 flex items-center gap-2">
              <FiMapPin size={20} className="text-primary" />
              Adresse de livraison
            </h2>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Entrez votre adresse complète..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Methode de paiement */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="font-bold text-gray-800 text-lg mb-4 flex items-center gap-2">
              <FiCreditCard size={20} className="text-primary" />
              Méthode de paiement
            </h2>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setPaymentMethod(method.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition ${
                    paymentMethod === method.id
                      ? 'border-primary bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full ${method.color}`} />
                  <span className="font-medium text-gray-700">{method.label}</span>
                  {paymentMethod === method.id && (
                    <FiCheck size={18} className="text-primary ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Résumé commande */}
        <div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
            <h2 className="font-bold text-gray-800 text-lg mb-4">
              Résumé de la commande
            </h2>
            <div className="space-y-3 mb-4">
              {cart.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {item.name} x{item.quantity} ({item.size})
                  </span>
                  <span className="font-semibold">
                    {(item.price * item.quantity).toLocaleString()} FCFA
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-4 mb-6">
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-primary">{cartTotal.toLocaleString()} FCFA</span>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm text-center">
                {error}
              </div>
            )}

            <button
              onClick={handleOrder}
              disabled={loading}
              className="w-full bg-secondary text-white py-4 rounded-xl font-bold text-lg hover:bg-orange-600 transition disabled:opacity-50"
            >
              {loading ? 'Traitement...' : `Payer ${cartTotal.toLocaleString()} FCFA`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;